import { Building, Languages, LogOut, Moon, Sun, UserIcon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@facade/components/ui";

import { CurrentUserAvatar } from "../CurrentUserAvatar";
import { signOut } from "aws-amplify/auth";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@facade/theme";
import { useTranslation } from "react-i18next";

const resolveTheme = () =>
  window.document.documentElement.classList.contains("dark") ? "dark" : "light";

const supportedLanguages = [
  {
    value: "pt-BR",
    label: "Português",
  },
  {
    value: "en",
    label: "English",
  },
] as const;

type SupportedLanguage = (typeof supportedLanguages)[number]["value"];

export const AccountMenu = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { theme, setTheme } = useTheme();

  const themeIcon = theme === "dark" ? <Moon /> : <Sun />;
  const currentLanguage = supportedLanguages.some(
    (language) => language.value === i18n.resolvedLanguage,
  )
    ? i18n.resolvedLanguage
    : "en";

  const handleSignout = async () => {
    await signOut();
    navigate("/signin");
  };

  const handleThemeChange = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();

    const theme = resolveTheme();
    setTheme(theme === "dark" ? "light" : "dark");
  };

  const handleLanguageChange = async (language: string) => {
    await i18n.changeLanguage(language as SupportedLanguage);

    localStorage.setItem("floorbit.language", language);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <CurrentUserAvatar />
      </DropdownMenuTrigger>

      <DropdownMenuContent className="min-w-56" align="end">
        <DropdownMenuGroup>
          <DropdownMenuItem onClick={() => navigate("/account")}>
            <UserIcon />
            {t("appbar.accountMenu.account")}
          </DropdownMenuItem>

          <DropdownMenuItem onClick={() => navigate("/account/organizations")}>
            <Building />
            Organizations
          </DropdownMenuItem>

          <DropdownMenuItem onClick={handleThemeChange}>
            {themeIcon}
            {t("appbar.accountMenu.theme")}
          </DropdownMenuItem>

          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Languages />
              {t("appbar.accountMenu.language")}
            </DropdownMenuSubTrigger>

            <DropdownMenuSubContent>
              <DropdownMenuRadioGroup
                value={currentLanguage}
                onValueChange={handleLanguageChange}
              >
                {supportedLanguages.map((language) => (
                  <DropdownMenuRadioItem
                    key={language.value}
                    value={language.value}
                  >
                    {language.label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        </DropdownMenuGroup>

        <DropdownMenuSeparator />

        <DropdownMenuGroup>
          <DropdownMenuItem onClick={handleSignout}>
            <LogOut />
            {t("appbar.accountMenu.signOut")}
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
